import React, {useEffect, useState} from 'react';
import {Button, HStack, Input, NumberInput, Switch, Text, VStack} from "@chakra-ui/react";
import {useForm} from "@/uses/useForm";
import {useApi} from "@/uses/useApi";
import {CreateOrEditForm, DatePicker, Field, FilesInput, LanguageField} from "@/components";
import {useLanguage} from "@/contexts/languageContext";

/** Délai proposé par défaut lorsqu'on active un rappel (en jours) */
const DEFAULT_REMINDER_DELAY = 3;

export const CreateOrEditPoll = (props) => {
    const pollId = props?.id;
    const api = useApi();
    const { getLangObject, t } = useLanguage();
    /** Un rappel est programmé dès que `reminderDelay` est renseigné */
    const [hasReminder, setHasReminder] = useState(false);
    const {formData, formFieldErrors, handleInputChange, handleInputChanges, handleFormSubmit} = useForm(
        {
            title: getLangObject(),
            fields: getLangObject([]),
            files: [],
            isAnonymous: false,
            endDate: null,
            reminderDelay: null,
            reminderIsRecurring: false,
        },
        {
            title: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length > 3)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            fields: {
                custom: {
                    function: (value) => {
                        return Object.values(value).every((value) => value && value.length > 0)
                    },
                    message: t('errors.pleaseFillFieldAllLanguages')
                },
            },
            reminderDelay: {
                custom: {
                    function: (value) => value === null || (Number.isInteger(value) && value > 0),
                    message: t('errors.positiveNumberOfDays')
                },
            },
        }
    );

    /**
     * Le rappel est piloté par `reminderDelay` : le désactiver efface la programmation.
     */
    const toggleReminder = (checked) => {
        setHasReminder(checked);
        handleInputChanges(checked
            ? {reminderDelay: DEFAULT_REMINDER_DELAY, reminderIsRecurring: false}
            : {reminderDelay: null, reminderIsRecurring: false}
        );
    };

    /**
     * Retrieve the poll when form is in edition mode
     */
    useEffect(() => {
        if (pollId) {
            api.ApiService.getOne('poll', pollId, {
                attributes: [
                    'id', 'title', 'fields', 'files', 'isAnonymous', 'endDate',
                    'reminderDelay', 'reminderIsRecurring'
                ],
            })
                .then(res => {
                    if (res?.data?.type === 'success' && res?.data?.value) {
                        const poll = res.data.value;
                        setHasReminder(poll.reminderDelay !== null && poll.reminderDelay !== undefined);
                        handleInputChanges({
                            ...poll,
                            /** Le DatePicker attend un objet Date, l'API renvoie une chaîne ISO */
                            endDate: poll.endDate ? new Date(poll.endDate) : null,
                        });
                    }
                })
                .catch(e => console.log(e?.response?.data))
        }
    }, [pollId])

    return (
        <VStack p="1.5rem 0" gap="5vh" w={{base: '90%', md: '78%'}}>
            <VStack flex={1} gap="1rem" p="1rem" w="100%" align="center" justify="flex-start" overflowY="hidden">
                <HStack w="100%" gap="1rem">
                    <LanguageField
                        flex={1}
                        id="title"
                        overflow="hidden"
                        errors={formFieldErrors.title}
                        label={t('wrappers.mandatoryLabel', { value: t('fields.title') })}
                        renderElement={(lang) => <Input
                            value={formData.title[lang]}
                            onChange={(e) => {
                                handleInputChange('title', {...formData.title, [lang]: e?.target?.value ?? ''})
                            }}
                            placeholder={t('polls.pollTitle')}
                        />}
                    />
                    <Field
                        label={t('wrappers.nonMandatoryLabel', { value: t('polls.anonymousPoll') })}
                        display="flex"
                        alignItems="center"
                        w="auto"
                    >
                        <Switch.Root
                            size="lg"
                            checked={formData.isAnonymous}
                            onCheckedChange={(e) => handleInputChange('isAnonymous', Boolean(e?.checked))}
                        >
                            <Switch.HiddenInput/>
                            <Switch.Control/>
                            <Switch.Label/>
                        </Switch.Root>
                    </Field>
                </HStack>
                <HStack w="100%" justify="flex-start" align="flex-end" gap="1.5rem" wrap="wrap">
                    <Field
                        w="unset"
                        label={t('wrappers.nonMandatoryLabel', { value: t('fields.endingDate') })}
                        errors={formFieldErrors.endDate}
                    >
                        <DatePicker
                            showTimeSelect={false}
                            date={formData.endDate}
                            onChange={(e) => handleInputChange('endDate', e)}
                            dateFormat={`EEE d MMM`}
                            label={t('fields.endingDate')}
                        />
                    </Field>
                    <Field
                        w="unset"
                        label={t('wrappers.nonMandatoryLabel', { value: t('polls.scheduleReminder') })}
                        display="flex"
                        alignItems="center"
                    >
                        <Switch.Root
                            size="lg"
                            checked={hasReminder}
                            onCheckedChange={(e) => toggleReminder(Boolean(e?.checked))}
                        >
                            <Switch.HiddenInput/>
                            <Switch.Control/>
                            <Switch.Label/>
                        </Switch.Root>
                    </Field>
                    {hasReminder && <>
                        <Field
                            w="7rem"
                            label={t('wrappers.mandatoryLabel', { value: t('polls.reminderDelay') })}
                            errors={formFieldErrors.reminderDelay}
                        >
                            <NumberInput.Root
                                min={1}
                                value={`${formData.reminderDelay ?? ''}`}
                                onValueChange={(e) => handleInputChange(
                                    'reminderDelay',
                                    Number.isNaN(e?.valueAsNumber) ? null : e?.valueAsNumber
                                )}
                            >
                                <NumberInput.Input placeholder={t('polls.reminderDelay')}/>
                            </NumberInput.Root>
                        </Field>
                        <Field
                            w="unset"
                            label={t('wrappers.nonMandatoryLabel', { value: t('polls.reminderIsRecurring') })}
                            display="flex"
                            alignItems="center"
                        >
                            <Switch.Root
                                size="lg"
                                checked={formData.reminderIsRecurring}
                                onCheckedChange={(e) => handleInputChange('reminderIsRecurring', Boolean(e?.checked))}
                            >
                                <Switch.HiddenInput/>
                                <Switch.Control/>
                                <Switch.Label/>
                            </Switch.Root>
                        </Field>
                    </>}
                </HStack>
                {hasReminder && <Text w="100%" fontSize="small" color="gray.500" mt="-0.5rem">
                    {formData.reminderIsRecurring
                        ? t('polls.reminderRecurringHelp', {count: formData.reminderDelay ?? 0})
                        : t('polls.reminderOnceHelp', {count: formData.reminderDelay ?? 0})}
                </Text>}
                <Field
                    w="100%"
                    label={t('wrappers.nonMandatoryLabel', { value: t('objects.image_other') })}
                >
                    <FilesInput
                        w="100%"
                        noLabel
                        multiple
                        accept="image/*"
                        files={formData.files ?? []}
                        onChange={(files) => handleInputChange('files', files)}
                    />
                </Field>
                <VStack flex={1} w="100%">
                    <CreateOrEditForm
                        fields={formData.fields}
                        onChange={(fields) => {
                            handleInputChange('fields', fields)
                        }}
                    />
                </VStack>

            </VStack>
            <Button
                variant="primary"
                mt="auto"
                height="auto"
                borderRadius=".5rem"
                style={{padding: "1rem 1.5rem"}}
                onClick={() => handleFormSubmit(async (data) => {
                    if (pollId) {
                        return api.ApiService.update('poll', pollId, data)
                    }
                    return api.ApiService.create('poll', data)
                }, props?.successCallback)}
            >
                {t('actions.validate')}
            </Button>
        </VStack>
    );
};

export default CreateOrEditPoll;
